import { Component, HostListener, AfterViewInit } from "@angular/core"
import { ApiService } from "@services/api.service"
import { NavigationEnd, Router, RouterEvent } from "@angular/router"
import { UserService } from "../services/user.service"
import { ModalService } from "../services/modal.service"
import { TokenService } from "../services/token.service"
import { BillingService } from "../services/billing.service"
import { FolderService } from "../services/folder.service"
import { GeneralService } from "../services/general/general.service"
import { environment } from "@environments/environment"
import { DeviceDetectorService } from "ngx-device-detector"
import { AnimationLoopService } from "@services/animationloop.service"
import { LoadingStatusService } from "@services/loadingstatus.service"
import { SupportChatService } from "@services/supportchat.service"
import { WindowService } from "@services/window.service"
import { enableAkitaProdMode } from "@datorama/akita"
import { playerQuery } from "../../../../common-lib/client-only/general/classes/playerStateManagement"
import { featureFlags } from "../../../../common-lib/general/utils/feature-flags"
import { ConfirmDiscardChangesService } from "@services/confirm-discard-changes.service"
import { Misc } from "@common-lib/modules/misc"
import { PlayerService } from "@services/audio/player.service"

export function isTracksListRoute(url: string): boolean {
    const path = url.split(/[?#]/)[0]

    return path === "" || path === "/" || /^\/mytracks(?:\/|$)/.test(path)
}

const countriesList = require("countries-list").countries
@Component({
    selector: "app-root",
    templateUrl: "app.component.html",
    styleUrls: ["app.component.scss"],
})
export class AppComponent implements AfterViewInit {
    errorMessage = ""
    isLoggedIn = false
    errorTimeout
    isUpdating = false

    paypalLoad: boolean = false

    tokenInterval = null

    loadingProgress = 0
    isInitialized = false

    constructionMode = null

    appSetupTimeout = null

    isMacElectronApp = false
    showDragAndDropUI = false
    editorIsOpen = false

    dragLeaveListener

    showChat = true

    deviceSpecificClass = ""

    isTracksListView = false
    songLyricsPanelOpen = false
    songLyricsPanelWidth = 400

    constructor(
        private loadingStatusService: LoadingStatusService,
        private device: DeviceDetectorService,
        private animationLoopService: AnimationLoopService,
        private generalService: GeneralService,
        private userService: UserService,
        private folderService: FolderService,
        private billingService: BillingService,
        private apiService: ApiService,
        private tokenService: TokenService,
        private modalService: ModalService,
        private router: Router,
        private supportChatService: SupportChatService,
        private windowService: WindowService,
        private readonly confirmDiscard: ConfirmDiscardChangesService,
        public playerService: PlayerService
    ) {
        this.apiService.isLoggedIn.subscribe(value => {
            this.isLoggedIn = value
        })

        this.updateRouteState(this.router.url)
        this.router.events.subscribe(event => {
            if (!(event instanceof NavigationEnd)) {
                return
            }

            this.editorIsOpen = event.urlAfterRedirects.includes("/editor")
            this.updateRouteState(event.urlAfterRedirects)
        })

        this.playerService.query.currentSong$.subscribe(currentSong => {
            if (currentSong === null) {
                this.closeSongLyricsPanel()
            }
        })

        this.supportChatService.setupCrisp()

        var user

        this.deviceSpecificClass = this.generalService.getDeviceSpecificClass()

        // to test our assumptions, I let it behave as if it is in prod
        // if (environment.production) {
        enableAkitaProdMode()
        // }

        localStorage.removeItem("geolocation")

        this.apiService
            .request(
                "/geolocation",
                { geolocation: "default" },
                "primary",
                "post",
                true
            )
            .then(async result => {
                this.setLoadingProgress(25)

                if (environment.production) {
                    this.apiService.apiURL = result.api
                    this.apiService.socketURL = result.websocket
                    this.apiService.backupAPIURL = result.backupAPI
                }

                this.tokenService.isInitialized.subscribe(value => {
                    if (!value) {
                        return
                    }

                    this.isInitialized = value
                })

                this.modalService.constructionMode.subscribe(value => {
                    if (this.constructionMode == true && value == false) {
                        location.reload()
                    }

                    this.constructionMode = value
                })

                this.userService.showChat.subscribe(value => {
                    this.showChat = value
                })

                if (
                    this.windowService.window.location.href.includes(
                        "redirectToApp=true"
                    )
                ) {
                    localStorage.setItem("redirectToApp", "true")
                }

                user = localStorage.getItem("currentUser")

                this.appSetupTimeout = setTimeout(() => {
                    location.reload()
                }, 180000)

                this.animationLoopService.addFunctionToLoop(
                    "getVersion",
                    this.modalService.getVersion.bind(this.modalService),
                    2 * 60 * 1000
                )

                return this.apiService.request(
                    "/getStartupData",
                    {},
                    "primary",
                    "post",
                    true
                )
            })

            .then(async res => {
                this.setLoadingProgress(55)

                this.apiService.realTimeSamplerVersion =
                    res.realTimeSamplerVersion

                this.billingService.countries.next(
                    AppComponent.processCountries()
                )

                var promise = Promise.resolve()

                if (user) {
                    this.apiService.isLoggedIn.next(true)
                    this.apiService.user.next(user)

                    promise = this.tokenService.generateToken()
                }

                return promise
            })

            .then(async () => {
                if (this.windowService.desktopAppAPI !== undefined) {
                    // Loading instruments first before finishing the app setup is necessary to
                    // prevent a race condition with the real time sampler
                    // fetch meta info for the desktop app ahead of time, so it can be used throughout the app
                    windowService.desktopAppInfo =
                        await windowService.generalAPI.getMetaInfo()
                }

                this.setLoadingProgress(75)

                await this.appSetup()

                if (this.appSetupTimeout) {
                    clearTimeout(this.appSetupTimeout)
                    this.appSetupTimeout = null
                }

                this.setLoadingProgress(95)
                this.tokenService.isInitialized.next(true)

                this.apiService.initSocketReconnectionHandler()

                this.loadingStatusService.init()
            })

            .catch(err => {
                console.error(err)
            })
    }

    private setLoadingProgress(progress: number) {
        this.loadingProgress = progress
    }

    public toggleSongLyricsPanel(): void {
        if (
            !this.isTracksListView ||
            this.playerService.query.currentSong === null
        ) {
            return
        }

        this.songLyricsPanelOpen = !this.songLyricsPanelOpen
    }

    public closeSongLyricsPanel(): void {
        this.songLyricsPanelOpen = false
    }

    public setSongLyricsPanelWidth(width: number): void {
        this.songLyricsPanelWidth = width
    }

    private updateRouteState(url: string): void {
        this.isTracksListView = isTracksListRoute(url)

        if (!this.isTracksListView) {
            this.closeSongLyricsPanel()
        }
    }

    static processCountries() {
        let billableCountries: any[] = []

        for (var country in countriesList) {
            countriesList[country].countryCode = country
            billableCountries.push(countriesList[country])
        }

        billableCountries.sort((a, b) => {
            if (a.name < b.name) {
                return -1
            }
            if (a.name > b.name) {
                return 1
            }

            return 0
        })

        billableCountries.unshift({
            countryCode: "",
            name: "Select a country",
            emoji: "",
        })

        return billableCountries
    }

    preventDropDefault(event) {
        event.preventDefault()
    }

    ngOnInit() {
        if (this.apiService.captcha.getValue() == null) {
            this.apiService.setupCaptcha()
        }
    }

    async ngAfterViewInit() {
        if (await this.windowService.isOutdatedApp()) {
            this.modalService.modals.outdatedDesktopApp.next(true)
        }

        if (this.windowService.desktopAppAPI !== undefined) {
            this.windowService.generalAPI.getMetaInfo().then(result => {
                this.isMacElectronApp =
                    result["platform"] == "darwin" ||
                    result["platform"] == "mac"
            })
        }

        window["$crisp"].push(["do", "chat:close"])
    }

    async initLoggedInUser(user: string) {
        this.initChatForLoggedInUser(user)

        if (this.tokenService.renewalInterval == null) {
            await this.tokenService.generateToken()
        }

        await this.apiService.socketSetup()

        let deviceType = "desktopBrowser"

        if (this.device.isMobile()) {
            deviceType = "mobile"
        } else if (this.windowService.desktopAppAPI !== undefined) {
            deviceType = "desktopApp"
        }

        const result = await this.apiService.authRequest(
            "/user/init",
            {
                deviceType,
                browserVersion: this.device.browser,
                operatingSystem: this.device.os,
            },
            "primary",
            true
        )

        this.userService.accountCreationDate = result.accountCreationDate

        this.folderService.setFolderContent({
            path: [],
            selectedFolderID: "",
            contentType: "Compositions",
            content: result.compositions,
            subFolders: result.folders,
        })

        this.userService.setProfileSettings(
            result.mixingOption,
            result.userSettings,
            result.modalSettings
        )
    }

    async initLoggedOutUser(user: string) {
        if (this.supportChatService.chatExists()) {
            this.supportChatService.showChatBubble()
        }

        this.userService.settings = null
        this.folderService.lastContentQuery = null

        localStorage.removeItem("token")
        localStorage.removeItem("refreshToken")

        this.tokenService.clearTokenRenewal()

        await this.apiService.socketSetup()
    }

    appSetup() {
        this.apiService.error.subscribe(err => {
            this.errorMessageBanner(err)
        })

        this.apiService.isUpdating.subscribe(isUpdating => {
            this.isUpdating = isUpdating
        })

        return new Promise((resolve, reject) => {
            // This is where we subscribe to get changes made to  the user object, which happens when
            // The user initializes the dashboard and the user logs in / creates an account
            // Also fires when logging out

            this.apiService.user.subscribe(async user => {
                try {
                    if (user != null) {
                        await this.initLoggedInUser(user)
                    } else {
                        await this.initLoggedOutUser(user)
                    }
                } catch (e) {
                    console.log("Error when setting up user: ")
                    console.error(e)
                }

                resolve(null)
            })
        })
    }

    initChatForLoggedInUser(user: string) {
        if (this.supportChatService.chatExists()) {
            if (this.supportChatService.chatIsOpen()) {
                this.supportChatService.showChatBubble()
            } else {
                this.supportChatService.hideChatBubble()
            }

            this.supportChatService.setEmailOnSupportChat(user)
        }
    }

    /*
		When apiService's error parameter is set to a new value, display the error message banner that lives in the navbar
	*/
    errorMessageBanner(err): void {
        if (err != "") {
            this.errorMessage = err

            clearTimeout(this.errorTimeout)
            this.errorTimeout = null

            this.errorTimeout = setTimeout(() => {
                this.errorMessage = ""
            }, 5000)
        }
    }

    cancelError(): void {
        this.errorMessage = ""
    }

    @HostListener("window:beforeunload", ["$event"])
    beforeUnloadEvent(event: KeyboardEvent) {
        if (this.windowService.desktopAppAPI === undefined) {
            return
        }

        this.windowService?.samplerAPI?.stopSampler()
    }

    @HostListener("document:keyup", ["$event"])
    @HostListener("document:keydown", ["$event"])
    handleKeyboardEvent(event: KeyboardEvent) {
        const tagName = document.activeElement.tagName

        if (tagName == "INPUT" || tagName == "TEXTAREA") {
            return
        }

        if (
            !environment.production &&
            event.key == "p" &&
            (event.metaKey || event.ctrlKey)
        ) {
            this.router.navigate(["potion"])
        } else if (
            !environment.production &&
            event.key == "d" &&
            event.ctrlKey
        ) {
            this.apiService.socket.getValue().disconnect()
        } else if (
            !environment.production &&
            event.key == "m" &&
            (event.metaKey || event.ctrlKey)
        ) {
            this.router.navigate(["sample-mapper"])
        } else if (
            featureFlags.enableDesktopAppBugReport &&
            this.windowService?.desktopAppAPI !== undefined &&
            event.key.toLowerCase() === "b" &&
            (event.ctrlKey || event.metaKey) &&
            event.shiftKey
        ) {
            this.modalService.modals.desktopAppBugReport$.next(true)
        } else if (!this.router.url.includes("/editor")) {
            // DO NOTHING

            if ((event.metaKey || event.ctrlKey) && event.key == "z") {
                event.preventDefault()
                event.stopPropagation()
            }
        } else if (this.router.url.includes("/editor")) {
            var state

            if (
                event != null &&
                event.key != null &&
                event.key.includes("Arrow")
            ) {
                event.preventDefault()
                event.stopPropagation()
            }

            if (event != null && event.type == "keyup") {
                return
            }

            return
        } else if (event.key === "d" && event.ctrlKey) {
            if (playerQuery.content === undefined) {
                return
            }

            const type =
                this.folderService.getContentType() == "Influences"
                    ? "influence"
                    : "composition"

            this.folderService
                .getContentByID(playerQuery.contentID, type)
                .then(composition => {
                    this.modalService.downloadComposition.next(composition)
                })
        }
    }

    dragOverHandler(event) {
        event.preventDefault()

        if (
            this.apiService.user.getValue() == null ||
            this.router.url.split("?")[0] == "/potion" ||
            this.router.url.split("?")[0] == "/sample-mapper" ||
            this.router.url.includes("generation-profile-editor") ||
            this.router.url.split("?")[0] == "/importjson" ||
            !this.generalService.containsFiles(event)
        ) {
            return
        }
    }

    toggleUploadInfluenceWindow() {
        this.router.navigate(["/create", "Upload influence"])
        this.userService.createTrackMenu.next("Upload influence")
    }

    refreshPage() {
        location.reload()
    }

    getDeviceSpecificClass() {
        const browser =
            this.device.browser == null ? "" : this.device.browser.toLowerCase()
        const os = this.device.os == null ? "" : this.device.os.toLowerCase()
        const device = this.device.isMobile() ? "mobile" : "desktop"

        return device + " " + os + " " + browser
    }
}
